import { makeAutoObservable, runInAction } from "mobx";
import { makePersistable } from "mobx-persist-store";
import { createClient } from "@/core/helpers";
import { ApiClient } from "@/clients/ApiClient";

export class SubjectStore {
    private client: ApiClient;
    public subjects: Subject[] = [];
    public subject: null | Subject = null;
    subjectsLoaded = false;

    constructor() {
        makeAutoObservable(this);
        makePersistable(this, {
            name: "SubjectStore",
            properties: ["subjects"],
            storage: window.localStorage,
            expireIn: 1000 * 60 * 60 * 24, // 24 hours
        });
        this.client = createClient();
    }

    /** Loads the list once per session; persisted copies only bridge the first render. */
    async ensureSubjectsLoaded() {
        if (this.subjectsLoaded) {
            return;
        }
        const { subjects } = await this.client.get<SubjectsResponse>(`/subjects`);
        runInAction(() => {
            this.subjects = subjects;
            this.subjectsLoaded = true;
        });
    }

    async loadSubject(slug: string) {
        this.subject = null;
        const { subject } = await this.client.get<SingleSubjectResponse>(`/subjects/${slug}`);
        this.subject = subject;
    }

    /**
     * Loads a subject by slug unless it is already the current one.
     * Throws when the slug does not exist so the page can show its error state.
     */
    async ensureSubjectLoaded(slug: string) {
        if (this.subject?.slug === slug) {
            return;
        }

        const { subject } = await this.client.get<SingleSubjectResponse>(`/subjects/${slug}`);
        if (!subject) {
            throw new Error(`Subject not found: ${slug}`);
        }
        runInAction(() => {
            this.subject = subject;
        });
    }
}
